import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  CheckCircle,
  Clock,
  Loader2,
  AlertCircle,
  Calendar,
} from 'lucide-react';
import clsx from 'clsx';
import FileTree from '../components/FileTree';
import FilePreview from '../components/FilePreview';
import { fetchGeneration, downloadGeneration } from '../lib/api';
import type { Generation, FileTreeNode } from '../types/wizard';

const statusConfig = {
  pending: {
    label: 'Pending',
    icon: Clock,
    color: 'text-gray-400',
    bg: 'bg-gray-500/10 border-gray-500/20',
  },
  generating: {
    label: 'Generating...',
    icon: Loader2,
    color: 'text-accent',
    bg: 'bg-accent/10 border-accent/20',
    animate: true,
  },
  completed: {
    label: 'Completed',
    icon: CheckCircle,
    color: 'text-green-400',
    bg: 'bg-green-500/10 border-green-500/20',
  },
  failed: {
    label: 'Failed',
    icon: AlertCircle,
    color: 'text-red-400',
    bg: 'bg-red-500/10 border-red-500/20',
  },
};

// Demo data
const demoFileTree: FileTreeNode[] = [
  { name: 'CLAUDE.md', path: 'CLAUDE.md', type: 'file' },
  {
    name: '.claude',
    path: '.claude',
    type: 'directory',
    children: [
      { name: 'settings.json', path: '.claude/settings.json', type: 'file' },
      {
        name: 'commands',
        path: '.claude/commands',
        type: 'directory',
        children: [
          { name: 'plan.md', path: '.claude/commands/plan.md', type: 'file' },
          { name: 'phase.md', path: '.claude/commands/phase.md', type: 'file' },
          { name: 'code-review.md', path: '.claude/commands/code-review.md', type: 'file' },
          { name: 'tdd.md', path: '.claude/commands/tdd.md', type: 'file' },
          { name: 'verify.md', path: '.claude/commands/verify.md', type: 'file' },
        ],
      },
      {
        name: 'agents',
        path: '.claude/agents',
        type: 'directory',
        children: [
          { name: 'planner.md', path: '.claude/agents/planner.md', type: 'file' },
          { name: 'code-reviewer.md', path: '.claude/agents/code-reviewer.md', type: 'file' },
          { name: 'architect.md', path: '.claude/agents/architect.md', type: 'file' },
        ],
      },
      {
        name: 'rules',
        path: '.claude/rules',
        type: 'directory',
        children: [
          {
            name: 'common',
            path: '.claude/rules/common',
            type: 'directory',
            children: [
              { name: 'coding-style.md', path: '.claude/rules/common/coding-style.md', type: 'file' },
              { name: 'security.md', path: '.claude/rules/common/security.md', type: 'file' },
              { name: 'testing.md', path: '.claude/rules/common/testing.md', type: 'file' },
              { name: 'git-workflow.md', path: '.claude/rules/common/git-workflow.md', type: 'file' },
            ],
          },
        ],
      },
    ],
  },
  { name: '.mcp.json', path: '.mcp.json', type: 'file' },
  {
    name: '.github',
    path: '.github',
    type: 'directory',
    children: [
      {
        name: 'workflows',
        path: '.github/workflows',
        type: 'directory',
        children: [
          { name: 'ci.yml', path: '.github/workflows/ci.yml', type: 'file' },
          { name: 'ai-code-audit.yml', path: '.github/workflows/ai-code-audit.yml', type: 'file' },
        ],
      },
      { name: 'PULL_REQUEST_TEMPLATE.md', path: '.github/PULL_REQUEST_TEMPLATE.md', type: 'file' },
      { name: 'CODEOWNERS', path: '.github/CODEOWNERS', type: 'file' },
    ],
  },
  {
    name: 'git-hooks',
    path: 'git-hooks',
    type: 'directory',
    children: [
      { name: 'pre-commit', path: 'git-hooks/pre-commit', type: 'file' },
      { name: 'commit-msg', path: 'git-hooks/commit-msg', type: 'file' },
      { name: 'pre-push', path: 'git-hooks/pre-push', type: 'file' },
    ],
  },
  {
    name: 'playbook',
    path: 'playbook',
    type: 'directory',
    children: [
      { name: 'PLAYBOOK.md', path: 'playbook/PLAYBOOK.md', type: 'file' },
      { name: 'phases', path: 'playbook/phases', type: 'directory', children: [] },
      { name: 'prompts', path: 'playbook/prompts', type: 'directory', children: [] },
    ],
  },
  {
    name: 'plugin',
    path: 'plugin',
    type: 'directory',
    children: [
      {
        name: 'codeforge-acme-platform',
        path: 'plugin/codeforge-acme-platform',
        type: 'directory',
        children: [
          { name: 'plugin.json', path: 'plugin/codeforge-acme-platform/.claude-plugin/plugin.json', type: 'file' },
          { name: 'skills', path: 'plugin/codeforge-acme-platform/skills', type: 'directory', children: [] },
          { name: 'agents', path: 'plugin/codeforge-acme-platform/agents', type: 'directory', children: [] },
        ],
      },
    ],
  },
  { name: '.eslintrc.js', path: '.eslintrc.js', type: 'file' },
  { name: '.prettierrc', path: '.prettierrc', type: 'file' },
];

const demoFileContents: Record<string, string> = {
  'CLAUDE.md': `# CLAUDE.md
# Generated by CodeForge for acme-platform

## Project Overview
E-commerce platform built with Next.js 14, TypeScript, and Prisma.
This is a greenfield project for the E-commerce / Retail industry.

## Compliance Requirements
- SOC 2: Ensure audit logging for all data access
- PCI DSS: Payment data handling must follow PCI DSS guidelines

## Build & Run Commands
- Install: \`npm install\`
- Dev: \`npm run dev\`
- Build: \`npm run build\`
- Test: \`npm test\`
- Lint: \`npm run lint\`

## Architecture Decisions
- Modular-monolith for maintainability and team scalability
- TypeScript with strict type checking
- PostgreSQL for ACID compliance
- Clean Architecture pattern

## Sensitive Paths (NEVER modify without human review)
- auth/*
- payments/*
- infra/*
- middleware.ts

## Claude Code Extensions
- **Commands**: .claude/commands/ — /plan, /tdd, /code-review, /phase, /verify
- **Agents**: .claude/agents/ — planner, code-reviewer, architect
- **Rules**: .claude/rules/ — auto-loaded coding standards, security, testing, git
- **Plugin**: plugin/codeforge-acme-platform/ — distributable project plugin
`,
  '.claude/settings.json': `{
  "permissions": {
    "allow": ["Bash(npm run lint)", "Bash(npm test)", "Bash(npm run build)"],
    "deny": ["Bash(rm -rf *)"]
  },
  "plugins": ["./plugin/codeforge-acme-platform"]
}`,
  '.mcp.json': `{
  "mcpServers": {
    "github": {
      "command": "npx",
      "args": ["-y", "@modelcontextprotocol/server-github"],
      "env": {
        "GITHUB_TOKEN": "\${GITHUB_TOKEN}"
      }
    },
    "jira": {
      "command": "npx",
      "args": ["-y", "@anthropic/mcp-server-jira"],
      "env": {
        "JIRA_API_TOKEN": "\${JIRA_API_TOKEN}",
        "JIRA_BASE_URL": "\${JIRA_BASE_URL}"
      }
    },
    "sentry": {
      "command": "npx",
      "args": ["-y", "@anthropic/mcp-server-sentry"],
      "env": {
        "SENTRY_AUTH_TOKEN": "\${SENTRY_AUTH_TOKEN}",
        "SENTRY_ORG": "\${SENTRY_ORG}"
      }
    }
  }
}`,
  '.github/workflows/ci.yml': `name: CI Pipeline
on:
  push:
    branches: [main, develop]
  pull_request:
    branches: [main]

concurrency:
  group: \${{ github.workflow }}-\${{ github.ref }}
  cancel-in-progress: true

jobs:
  lint:
    name: Lint & Type Check
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
      - uses: actions/setup-node@v4
        with:
          node-version: 20
          cache: 'npm'
      - run: npm ci
      - run: npm run lint
      - run: npm run type-check

  test:
    name: Test
    runs-on: ubuntu-latest
    needs: lint
    services:
      postgres:
        image: postgres:16
        env:
          POSTGRES_DB: test
          POSTGRES_PASSWORD: test
        ports:
          - 5432:5432
    steps:
      - uses: actions/checkout@v4
      - uses: actions/setup-node@v4
        with:
          node-version: 20
          cache: 'npm'
      - run: npm ci
      - run: npm run test -- --coverage
        env:
          DATABASE_URL: postgresql://postgres:test@localhost:5432/test
      - uses: codecov/codecov-action@v4
        with:
          fail_ci_if_error: true

  build:
    name: Build
    runs-on: ubuntu-latest
    needs: test
    steps:
      - uses: actions/checkout@v4
      - uses: actions/setup-node@v4
        with:
          node-version: 20
          cache: 'npm'
      - run: npm ci
      - run: npm run build
      - uses: actions/upload-artifact@v4
        with:
          name: build
          path: .next/
          retention-days: 7
`,
  '.github/workflows/ai-code-audit.yml': `name: AI Code Review Flag
on:
  pull_request:
    types: [opened, synchronize]

jobs:
  check-ai-code:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
        with:
          fetch-depth: 0
      - name: Check for AI-generated code markers
        run: |
          if git log --format='%B' origin/main..HEAD | grep -qi "\\[ai-generated\\]\\|\\[claude\\]\\|\\[copilot\\]"; then
            echo "::warning::This PR contains AI-generated code. Ensure thorough review."
            echo "AI_CODE_DETECTED=true" >> $GITHUB_ENV
          fi
      - name: Add label if AI code detected
        if: env.AI_CODE_DETECTED == 'true'
        uses: actions/github-script@v7
        with:
          script: |
            github.rest.issues.addLabels({
              issue_number: context.issue.number,
              owner: context.repo.owner,
              repo: context.repo.repo,
              labels: ['ai-generated']
            })
`,
  '.github/PULL_REQUEST_TEMPLATE.md': `## Summary
<!-- Brief description of changes -->

## Type of Change
- [ ] Bug fix
- [ ] New feature
- [ ] Breaking change
- [ ] Documentation update
- [ ] Refactoring

## Related Tickets
<!-- Link to Jira/Linear tickets -->

## AI Disclosure
- [ ] This PR contains AI-generated code
- [ ] AI-generated code has been reviewed and tested

## Testing
- [ ] Unit tests added/updated
- [ ] Integration tests added/updated
- [ ] Manual testing performed

## Checklist
- [ ] Code follows project coding standards
- [ ] Self-review completed
- [ ] Documentation updated (if applicable)
- [ ] No sensitive paths modified without approval
- [ ] Coverage threshold maintained (>= 80%)
`,
  '.github/CODEOWNERS': `# CODEOWNERS - Generated by CodeForge
# Global code owners
* @team-leads

# Frontend
src/components/** @frontend-team
src/app/** @frontend-team
src/hooks/** @frontend-team

# Backend / API
src/server/** @backend-team
src/api/** @backend-team
prisma/** @backend-team

# Security-sensitive paths (require additional review)
auth/** @security-team @team-leads
payments/** @security-team @team-leads
middleware.ts @security-team @team-leads

# Infrastructure
infra/** @devops-team @team-leads
.github/workflows/** @devops-team
Dockerfile @devops-team
docker-compose*.yml @devops-team

# Configuration
.eslintrc* @tech-leads
.prettierrc @tech-leads
tsconfig*.json @tech-leads
`,
  'git-hooks/pre-commit': `#!/bin/sh
# CodeForge pre-commit hook

npx lint-staged
`,
  'git-hooks/commit-msg': `#!/bin/sh
# CodeForge commit-msg hook

npx --no-install commitlint --edit "$1"
`,
  '.eslintrc.js': `{
  "extends": [
    "next/core-web-vitals",
    "plugin:@typescript-eslint/strict",
    "prettier"
  ],
  "parser": "@typescript-eslint/parser",
  "plugins": ["@typescript-eslint", "import"],
  "rules": {
    "@typescript-eslint/no-unused-vars": "error",
    "@typescript-eslint/no-explicit-any": "error",
    "@typescript-eslint/strict-boolean-expressions": "warn",
    "@typescript-eslint/consistent-type-imports": "error",
    "import/order": ["error", {
      "groups": ["builtin", "external", "internal", "parent", "sibling"],
      "newlines-between": "always",
      "alphabetize": { "order": "asc" }
    }],
    "no-console": ["warn", { "allow": ["warn", "error"] }],
    "max-lines": ["warn", { "max": 300 }]
  }
}`,
  '.prettierrc': `{
  "semi": true,
  "singleQuote": true,
  "trailingComma": "all",
  "tabWidth": 2,
  "printWidth": 100,
  "bracketSpacing": true,
  "arrowParens": "always",
  "endOfLine": "lf"
}`,
  'tsconfig.json': `{
  "compilerOptions": {
    "target": "ES2022",
    "lib": ["dom", "dom.iterable", "esnext"],
    "allowJs": true,
    "skipLibCheck": true,
    "strict": true,
    "noEmit": true,
    "esModuleInterop": true,
    "module": "esnext",
    "moduleResolution": "bundler",
    "resolveJsonModule": true,
    "isolatedModules": true,
    "jsx": "preserve",
    "incremental": true,
    "plugins": [{ "name": "next" }],
    "paths": {
      "@/*": ["./src/*"]
    }
  },
  "include": ["next-env.d.ts", "**/*.ts", "**/*.tsx", ".next/types/**/*.ts"],
  "exclude": ["node_modules"]
}`,

  // --- Commands ---

  '.claude/commands/plan.md': `# /plan — Generate an Implementation Plan

Analyze the request and produce a structured implementation plan.

## Steps

1. Understand the requirement from the user's description
2. Identify affected files and modules
3. Propose an ordered list of changes
4. Flag any sensitive paths that need human review
5. Estimate testing requirements

## Output Format

Return a numbered plan with file paths, change descriptions, and risk flags.
`,

  '.claude/commands/phase.md': `# /phase — Project Phase Management

Check or advance the current project phase.

## Usage

- \`/phase\` or \`/phase status\` — Show current phase, objectives, and progress
- \`/phase next\` — Advance to the next phase (after quality gates pass)

## Behavior

Read from playbook/current-phase.json and display phase details.
When advancing, verify quality gates are met before proceeding.
`,

  '.claude/commands/code-review.md': `# /code-review — AI-Assisted Code Review

Review the specified code changes for quality, security, and correctness.

## Checklist

1. **Correctness** — Does the code do what it claims?
2. **Security** — Any injection, XSS, or auth bypass risks?
3. **Performance** — Obvious N+1 queries, missing indexes?
4. **Style** — Follows project conventions in CLAUDE.md?
5. **Tests** — Adequate coverage for the change?
6. **Types** — Proper TypeScript types, no \`any\`?

## Output Format

For each file changed, provide:
- **Verdict**: approve / request-changes / comment
- **Issues**: numbered list with severity
- **Suggestions**: concrete code improvements
`,

  '.claude/commands/tdd.md': `# /tdd — Test-Driven Development Workflow

Write code using strict TDD (Red-Green-Refactor).

## Workflow

1. **Red**: Write a failing test for the requirement
2. **Green**: Write the minimum code to make the test pass
3. **Refactor**: Clean up while keeping tests green

## Rules

- NEVER write implementation before the test
- Each cycle should take < 5 minutes
- Run \`npm test\` after each step to verify
- Commit after each green-refactor cycle
`,

  '.claude/commands/verify.md': `# /verify — Full Project Verification Pipeline

Run the complete verification pipeline for the project.

## Arguments

Dispatch based on $ARGUMENTS:
- \`quick\` — build + type-check only
- \`full\` (default) — all checks
- \`pre-commit\` — build, types, lint, console.log audit
- \`pre-pr\` — all checks plus security scan

## Steps (full)

1. \`npm run build\` — production build succeeds
2. \`npx tsc --noEmit\` — zero type errors
3. \`npm run lint\` — zero lint errors
4. \`npm test\` — all tests pass
5. Audit for \`console.log\` statements
6. Check for \`any\` types
7. Verify no secrets in staged files
`,

  // --- Agents ---

  '.claude/agents/planner.md': `---
name: planner
description: |
  Expert planning specialist for breaking down features into
  implementation steps. Analyzes requirements, identifies risks,
  and produces actionable development plans.

  <example>
  Context: Planning a new feature.
  user: "Plan the implementation of user authentication with OAuth."
  assistant: "I'll use the Task tool to launch the planner agent."
  </example>
tools: Read, Grep, Glob, WebSearch
model: opus
color: green
---

# Planner Agent

You break down complex features into clear, ordered implementation steps.

## Principles
- Identify module boundaries and cross-cutting concerns
- Estimate complexity and suggest reviewers
- Always include test requirements in plans
- Flag sensitive paths for human review
`,

  '.claude/agents/code-reviewer.md': `---
name: code-reviewer
description: |
  Expert code review specialist for TypeScript projects.
  Performs deep analysis for correctness, security,
  performance, and adherence to coding standards.

  <example>
  Context: A PR with auth changes needs review.
  user: "Review the changes in src/auth/ for security issues."
  assistant: "I'll use the Task tool to launch the code-reviewer agent."
  </example>
tools: Read, Grep, Glob
model: sonnet
color: red
---

# Code Reviewer Agent

You review code with the rigor of a senior engineer.

## Focus Areas
- Type safety and proper TypeScript usage
- Security vulnerabilities (OWASP Top 10)
- Performance anti-patterns
- Test coverage gaps
`,

  '.claude/agents/architect.md': `---
name: architect
description: |
  System architecture specialist for designing scalable,
  maintainable software systems following clean architecture.

  <example>
  Context: Adding real-time features to the platform.
  user: "Design the architecture for WebSocket support."
  assistant: "I'll use the Task tool to launch the architect agent."
  </example>
tools: Read, Grep, Glob, WebSearch
model: opus
color: cyan
---

# Architect Agent

You design systems that are simple, scalable, and maintainable.

## Output Format
1. Context and constraints
2. Proposed architecture
3. Module boundaries and data flow
4. Trade-offs and alternatives
5. Migration path (if needed)
`,

  // --- Rules ---

  '.claude/rules/common/coding-style.md': `# Coding Style Rules (Auto-loaded)

## Naming Conventions
- Files: \`kebab-case.ts\`
- Classes/Types: \`PascalCase\`
- Functions/Variables: \`camelCase\`
- Constants: \`SCREAMING_SNAKE_CASE\`
- Database tables: \`snake_case\`
- API endpoints: \`kebab-case\`

## Code Quality
- Maximum file length: 300 lines
- No \`any\` types — use proper TypeScript types
- No \`console.log\` — use structured logger
- Prefer named exports over default exports
- Use early returns to reduce nesting
`,

  '.claude/rules/common/security.md': `# Security Rules (Auto-loaded)

## Sensitive Paths — Require Senior Review
- \`/auth\` — Authentication & session management
- \`/payments\` — Payment processing & billing
- \`/migrations\` — Database schema changes

## What Claude Code Should NEVER Do
1. Commit directly to \`main\` or \`develop\`
2. Remove or weaken existing tests
3. Add new dependencies without documenting why
4. Disable linting rules or type checking
5. Hardcode secrets, API keys, or credentials
6. Skip error handling for external calls
`,

  '.claude/rules/common/testing.md': `# Testing Rules (Auto-loaded)

## Test Standards
- Minimum 80% line coverage for all new code
- Every bug fix must include a regression test
- Tests must be deterministic — no flaky tests

## Test Structure
- Use \`describe\` blocks to group related tests
- Use \`it('should ...')\` for descriptive test names
- Follow Arrange-Act-Assert pattern
- One assertion per test when possible

## What to Test
- All business logic and domain rules
- API endpoint request/response contracts
- Error handling and edge cases
`,

  '.claude/rules/common/git-workflow.md': `# Git Workflow Rules (Auto-loaded)

## Branch Naming
\`<type>/<ticket>-<short-description>\`
Example: \`feat/CF-42-add-oauth-login\`

## Commit Messages
Follow Conventional Commits:
\`<type>(<scope>): <description>\`
Example: \`feat(auth): add OAuth2 login flow\`

## Pull Request Rules
- Keep PRs small and focused (< 400 lines)
- Include a description of WHY, not just WHAT
- Link to the relevant ticket/issue
- All CI checks must pass before merge
`,

  // --- Other missing files ---

  'git-hooks/pre-push': `#!/bin/sh
# CodeForge pre-push hook

echo "Running pre-push checks..."
npm test
echo "Pre-push checks passed."
`,

  'playbook/PLAYBOOK.md': `# Acme Platform — Project Playbook

## Overview
Phased approach for building the Acme Platform e-commerce system.

## Phases
1. **Project Setup** — Repository, tooling, CI/CD
2. **Foundation** — Core architecture, database, auth
3. **Core Features** — Business logic and API
4. **Frontend** — UI components and pages
5. **Launch** — Production deployment and monitoring

## Usage
\`\`\`bash
claude /phase status
claude /phase next
\`\`\`
`,

  'plugin/codeforge-acme-platform/.claude-plugin/plugin.json': `{
  "name": "codeforge-acme-platform",
  "version": "1.0.0",
  "description": "CodeForge development harness for Acme Platform",
  "author": {
    "name": "CodeForge",
    "email": "hello@codeforge.dev"
  },
  "skills": ["./skills/"],
  "commands": ["./commands/"],
  "agents": [
    "./agents/planner.md",
    "./agents/code-reviewer.md",
    "./agents/architect.md"
  ]
}`,
};

const demoGeneration: Generation = {
  id: 'gen-1',
  name: 'Acme Platform',
  status: 'completed',
  createdAt: '2024-11-20T14:30:00Z',
  completedAt: '2024-11-20T14:31:15Z',
  projectContext: {
    name: 'acme-platform',
    goal: 'Greenfield Project',
    description: 'E-commerce platform with Next.js and TypeScript',
    industry: 'E-commerce / Retail',
    compliance: ['SOC 2', 'PCI DSS'],
  },
  wizardData: {} as Generation['wizardData'],
};

export default function GenerationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [generation, setGeneration] = useState<Generation>(demoGeneration);
  const [selectedFile, setSelectedFile] = useState<string>('CLAUDE.md');
  const [loading, setLoading] = useState(false);

  const [realFiles, setRealFiles] = useState<Record<string, string> | null>(null);
  const [realFileTree, setRealFileTree] = useState<FileTreeNode[] | null>(null);

  useEffect(() => {
    const loadGeneration = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const data = await fetchGeneration(id);
        setGeneration(data);
        if (data.outputFiles && typeof data.outputFiles === 'object') {
          setRealFiles(data.outputFiles);
          setRealFileTree(buildFileTree(data.outputFiles));
          const firstFile = Object.keys(data.outputFiles)[0];
          if (firstFile) setSelectedFile(firstFile);
        }
      } catch {
        // Use demo data
      } finally {
        setLoading(false);
      }
    };
    loadGeneration();
  }, [id]);

  const handleDownload = async () => {
    if (!id) return;
    try {
      const blob = await downloadGeneration(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `codeforge-${generation.name.toLowerCase().replace(/\s+/g, '-')}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // Download not available in demo
    }
  };

  const status = statusConfig[generation.status];
  const StatusIcon = status.icon;
  const fileContent = realFiles?.[selectedFile] ?? demoFileContents[selectedFile] ?? '// File content not available';

  if (loading) {
    return (
      <div className="min-h-screen pt-28 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-surface-200 rounded w-1/3" />
            <div className="h-4 bg-surface-200 rounded w-2/3" />
            <div className="h-96 bg-surface-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back */}
        <Link
          to="/generations"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-300 mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Generations
        </Link>

        {/* Header */}
        <div className="card mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-white">
                  {generation.name}
                </h1>
                <span
                  className={clsx(
                    'badge border',
                    status.bg,
                    status.color
                  )}
                >
                  <StatusIcon
                    className={clsx(
                      'h-3 w-3 mr-1',
                      (status as { animate?: boolean }).animate && 'animate-spin'
                    )}
                  />
                  {status.label}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  Created {new Date(generation.createdAt).toLocaleString()}
                </span>
                {generation.completedAt && (
                  <span>
                    &middot; Completed{' '}
                    {new Date(generation.completedAt).toLocaleString()}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="px-2.5 py-1 text-xs rounded bg-surface-100 text-gray-400 border border-dark-border">
                  {generation.projectContext.goal}
                </span>
                <span className="px-2.5 py-1 text-xs rounded bg-surface-100 text-gray-400 border border-dark-border">
                  {generation.projectContext.industry}
                </span>
                {generation.projectContext.compliance.map((c) => (
                  <span
                    key={c}
                    className="px-2.5 py-1 text-xs rounded bg-amber-500/10 text-amber-400 border border-amber-500/20"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>
            {generation.status === 'completed' && (
              <button onClick={handleDownload} className="btn-primary shrink-0">
                <Download className="h-4 w-4" />
                Download ZIP
              </button>
            )}
          </div>
        </div>

        {/* File Explorer */}
        {generation.status === 'completed' && (
          <div className="grid grid-cols-[280px_1fr] gap-4 h-[640px]">
            {/* File tree */}
            <div className="card overflow-auto p-3">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-2 mb-2">
                Generated Files
              </div>
              <FileTree
                nodes={realFileTree || demoFileTree}
                selectedPath={selectedFile}
                onSelect={(path) => setSelectedFile(path)}
              />
            </div>

            {/* File preview */}
            <FilePreview path={selectedFile} content={fileContent} />
          </div>
        )}

        {generation.status === 'generating' && (
          <div className="card text-center py-20">
            <Loader2 className="h-12 w-12 text-accent animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">
              Generating Your Blueprint
            </h2>
            <p className="text-gray-500 max-w-md mx-auto">
              We are building your complete organizational blueprint.
              This usually takes 30-60 seconds.
            </p>
            <div className="mt-8 max-w-xs mx-auto">
              <div className="h-2 bg-surface-200 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-accent to-blue-400 rounded-full animate-pulse w-2/3" />
              </div>
            </div>
          </div>
        )}

        {generation.status === 'failed' && (
          <div className="card text-center py-20">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">
              Generation Failed
            </h2>
            <p className="text-gray-500 max-w-md mx-auto mb-2">
              Something went wrong while generating your environment.
            </p>
            {generation.error && (
              <p className="text-sm text-red-400 font-mono bg-red-500/5 border border-red-500/20 rounded-lg px-4 py-3 max-w-lg mx-auto mt-4">
                {generation.error}
              </p>
            )}
            <Link to="/wizard" className="btn-primary mt-8">
              Try Again
            </Link>
          </div>
        )}

        {generation.status === 'pending' && (
          <div className="card text-center py-20">
            <Clock className="h-12 w-12 text-gray-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">
              Generation Pending
            </h2>
            <p className="text-gray-500 max-w-md mx-auto">
              Your generation is queued and will start processing shortly.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// Build a FileTreeNode[] from a flat Record<string, string> of file paths
function buildFileTree(files: Record<string, string>): FileTreeNode[] {
  const root: FileTreeNode[] = [];

  for (const filePath of Object.keys(files).sort()) {
    const parts = filePath.split('/');
    let current = root;

    for (let i = 0; i < parts.length; i++) {
      const name = parts[i];
      const path = parts.slice(0, i + 1).join('/');
      const isFile = i === parts.length - 1;

      let existing = current.find((n) => n.name === name);
      if (!existing) {
        existing = {
          name,
          path,
          type: isFile ? 'file' : 'directory',
          children: isFile ? undefined : [],
        };
        current.push(existing);
      }
      if (!isFile && existing.children) {
        current = existing.children;
      }
    }
  }

  return root;
}
